import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { ReactNode, useMemo } from "react";
import { theme } from "src/assets/theme";

interface StripeContextProviderProps {
  children: ReactNode;
  billingModel?: "one_time" | "subscription";
  amount?: number;
  customerSessionToken?: string;
}

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "",
);

const StripeContextProvider = ({
  children,
  billingModel,
  amount,
  customerSessionToken,
}: StripeContextProviderProps) => {
  const appearance: StripeElementsOptions["appearance"] = useMemo(
    () => ({
      theme: "stripe",
      variables: {
        colorPrimary: theme.palette.primary.main,
        borderRadius: "10px",
      },
    }),
    [],
  );

  // Properly type the options object
  const stripeOptions: StripeElementsOptions = useMemo(() => {
    const paymentMethods = ["card"];

    if (billingModel === "one_time") {
      paymentMethods.push("klarna");
    }

    const price = (amount ?? 0) * 100;

    return {
      appearance,
      currency: "usd",
      mode: billingModel === "one_time" ? "payment" : "setup",
      amount: price,
      customerSessionClientSecret: customerSessionToken,
      payment_method_types: paymentMethods,
      setup_future_usage:
        billingModel === "subscription" ? "off_session" : "on_session",
    };
  }, [billingModel, amount, appearance, customerSessionToken]);

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      {children}
    </Elements>
  );
};

export default StripeContextProvider;
